/**
 * i18n-toasts.js — tiny translations table for the toast strings we surface
 * across the app. Keeps the payload out of every component and makes it
 * trivial to add new language rows.
 *
 * Supported languages match Kimani's polyglot set:
 *   EN (default) · SW (Kiswahili) · SH (Sheng) · FR · AR · PT
 *
 * Usage (function form):
 *   import { t } from "@/lib/i18n-toasts";
 *   toast.error(t("payment_failed", user?.preferred_language));
 *
 * Usage (hook form):
 *   const { t } = useToastCopy();
 *   toast.success(t("saved"));
 *
 * If a language row is missing a key, the function silently falls back to EN
 * so we never crash on a partial translation.
 */
import { useAuth } from "@/context/AuthContext";

// Table shape: { [key]: { EN, SW, SH, FR, AR, PT } }
const TABLE = {
    // ── reCAPTCHA + bot guard ────────────────────────────────────────────
    recaptcha_low_score: {
        EN: "We couldn't verify you're human — please refresh and try again.",
        SW: "Hatukuweza kukuthibitisha kama binadamu — tafadhali onyesha upya na ujaribu tena.",
        SH: "Hatukuweza kukuthibitisha kama binadamu — tafadhali onyesha upya na ujaribu tena.",
        FR: "Impossible de vérifier que vous êtes humain — veuillez actualiser et réessayer.",
        AR: "تعذّر التحقق من أنك إنسان — يرجى تحديث الصفحة والمحاولة مرة أخرى.",
        PT: "Não foi possível verificar se você é humano — atualize a página e tente novamente.",
    },
    recaptcha_unavailable: {
        EN: "Bot-protection service is unavailable. Please retry in a moment.",
        SW: "Huduma ya kulinda dhidi ya boti haipatikani. Tafadhali jaribu tena baada ya muda.",
        SH: "Huduma ya kulinda dhidi ya boti haipatikani. Jaribu tena baadaye.",
        FR: "Le service de protection anti-bot est indisponible. Veuillez réessayer dans un instant.",
        AR: "خدمة الحماية من الروبوتات غير متاحة. يرجى المحاولة بعد قليل.",
        PT: "O serviço de proteção contra bots está indisponível. Tente novamente em instantes.",
    },
    // ── Payment / checkout ───────────────────────────────────────────────
    payment_failed: {
        EN: "Checkout failed — please retry.",
        SW: "Malipo hayakufaulu — tafadhali jaribu tena.",
        SH: "Payment ime-fail — jaribu tena.",
        FR: "Le paiement a échoué — veuillez réessayer.",
        AR: "فشلت عملية الدفع — يرجى المحاولة مرة أخرى.",
        PT: "O pagamento falhou — tente novamente.",
    },
    payment_url_missing: {
        EN: "Stripe did not return a checkout URL — please retry.",
        SW: "Stripe haikurudisha kiungo cha malipo — tafadhali jaribu tena.",
        SH: "Stripe haija-return checkout URL — jaribu tena.",
        FR: "Stripe n'a pas renvoyé d'URL de paiement — veuillez réessayer.",
        AR: "لم يُرجع Stripe رابط دفع — يرجى المحاولة مرة أخرى.",
        PT: "O Stripe não retornou uma URL de checkout — tente novamente.",
    },
    pricing_load_failed: {
        EN: "Could not load pricing — please retry.",
        SW: "Bei haikuweza kupakuliwa — tafadhali jaribu tena.",
        SH: "Bei haija-load — jaribu tena.",
        FR: "Impossible de charger la tarification — veuillez réessayer.",
        AR: "تعذّر تحميل الأسعار — يرجى المحاولة مرة أخرى.",
        PT: "Não foi possível carregar os preços — tente novamente.",
    },
    // ── Paybill submission ───────────────────────────────────────────────
    paybill_submitted: {
        EN: "Submitted for verification",
        SW: "Imewasilishwa kwa uthibitishaji",
        SH: "Imewasilishwa kwa uthibitisho",
        FR: "Soumis pour vérification",
        AR: "تم الإرسال للتحقق",
        PT: "Enviado para verificação",
    },
    paybill_ref_missing: {
        EN: "Please enter the M-Pesa confirmation code.",
        SW: "Tafadhali ingiza msimbo wa uthibitisho wa M-Pesa.",
        SH: "Weka M-Pesa code yako.",
        FR: "Veuillez saisir le code de confirmation M-Pesa.",
        AR: "يرجى إدخال رمز تأكيد M-Pesa.",
        PT: "Digite o código de confirmação do M-Pesa.",
    },
    // ── Onboarding / psychometric ────────────────────────────────────────
    onboarding_load_failed: {
        EN: "Could not load the psychometric baseline — please retry.",
        SW: "Tathmini haikuweza kupakuliwa — tafadhali jaribu tena.",
        SH: "Tathmini haija-load — jaribu tena.",
        FR: "Impossible de charger le bilan psychométrique — veuillez réessayer.",
        AR: "تعذّر تحميل التقييم النفسي — يرجى المحاولة مرة أخرى.",
        PT: "Não foi possível carregar a avaliação psicométrica — tente novamente.",
    },
    onboarding_submit_failed: {
        EN: "Submission failed — please retry.",
        SW: "Kutuma kumeshindikana — tafadhali jaribu tena.",
        SH: "Kutuma ime-fail — jaribu tena.",
        FR: "L'envoi a échoué — veuillez réessayer.",
        AR: "فشل الإرسال — يرجى المحاولة مرة أخرى.",
        PT: "O envio falhou — tente novamente.",
    },
    // ── Chat widget generic ──────────────────────────────────────────────
    chat_unreachable: {
        EN: "Sorry — I'm having trouble reaching the assistant right now. Try again in a moment, or email info@meliandialogue.com.",
        SW: "Samahani — nashindwa kufikia msaidizi sasa. Jaribu tena baadaye au tuma barua info@meliandialogue.com.",
        SH: "Pole — nashindwa kufikia assistant saa hii. Jaribu tena baadaye au email info@meliandialogue.com.",
        FR: "Désolé — impossible de joindre l'assistant pour le moment. Réessayez dans un instant ou envoyez un e-mail à info@meliandialogue.com.",
        AR: "عذرًا — أواجه مشكلة في الوصول إلى المساعد الآن. حاول مرة أخرى بعد قليل أو راسلنا على info@meliandialogue.com.",
        PT: "Desculpe — não consegui acessar o assistente agora. Tente novamente em instantes ou envie um e-mail para info@meliandialogue.com.",
    },
    // ── Common save / update / delete flows ─────────────────────────────
    saved: {
        EN: "Saved",
        SW: "Imehifadhiwa",
        SH: "Saved",
        FR: "Enregistré",
        AR: "تم الحفظ",
        PT: "Salvo",
    },
    save_failed: {
        EN: "Save failed — please retry.",
        SW: "Kuhifadhi kumeshindikana — tafadhali jaribu tena.",
        SH: "Save ime-fail — jaribu tena.",
        FR: "Échec de l'enregistrement — veuillez réessayer.",
        AR: "فشل الحفظ — يرجى المحاولة مرة أخرى.",
        PT: "Falha ao salvar — tente novamente.",
    },
    copied: {
        EN: "Copied to clipboard",
        SW: "Imenakiliwa",
        SH: "Copied",
        FR: "Copié dans le presse-papiers",
        AR: "تم النسخ إلى الحافظة",
        PT: "Copiado para a área de transferência",
    },
    copy_failed: {
        EN: "Copy failed — long-press to copy manually.",
        SW: "Kunakili kumeshindikana — bofya kwa muda mrefu ili kunakili.",
        SH: "Copy ime-fail — long-press ku-copy manually.",
        FR: "Échec de la copie — appuyez longuement pour copier manuellement.",
        AR: "فشل النسخ — اضغط مطولًا للنسخ يدويًا.",
        PT: "Falha ao copiar — pressione e segure para copiar manualmente.",
    },
    load_failed_generic: {
        EN: "Could not load — please retry.",
        SW: "Haikuweza kupakuliwa — tafadhali jaribu tena.",
        SH: "Haija-load — jaribu tena.",
        FR: "Impossible de charger — veuillez réessayer.",
        AR: "تعذّر التحميل — يرجى المحاولة مرة أخرى.",
        PT: "Não foi possível carregar — tente novamente.",
    },
    session_expired: {
        EN: "Session expired — please sign in again.",
        SW: "Kikao kimemalizika — tafadhali ingia tena.",
        SH: "Session ime-expire — sign in tena.",
        FR: "Session expirée — veuillez vous reconnecter.",
        AR: "انتهت الجلسة — يرجى تسجيل الدخول مرة أخرى.",
        PT: "Sessão expirada — entre novamente.",
    },
    network_error: {
        EN: "Network error — check your connection and retry.",
        SW: "Hitilafu ya mtandao — angalia muunganisho wako na ujaribu tena.",
        SH: "Network error — angalia connection na jaribu tena.",
        FR: "Erreur réseau — vérifiez votre connexion et réessayez.",
        AR: "خطأ في الشبكة — تحقق من الاتصال وحاول مرة أخرى.",
        PT: "Erro de rede — verifique sua conexão e tente novamente.",
    },
    permission_denied: {
        EN: "Permission denied.",
        SW: "Ruhusa imekataliwa.",
        SH: "Permission denied.",
        FR: "Autorisation refusée.",
        AR: "تم رفض الإذن.",
        PT: "Permissão negada.",
    },
    tier_upgrade_required: {
        EN: "This feature is available on higher tiers. Upgrade to unlock.",
        SW: "Kipengele hiki kinapatikana kwa viwango vya juu. Boresha ili kufungua.",
        SH: "Feature hii ni ya juu tiers. Upgrade ku-unlock.",
        FR: "Cette fonctionnalité est réservée aux paliers supérieurs. Passez à un plan supérieur pour la débloquer.",
        AR: "هذه الميزة متاحة في الفئات الأعلى. قم بالترقية لفتحها.",
        PT: "Este recurso está disponível em planos superiores. Faça upgrade para desbloquear.",
    },
    logged_out: {
        EN: "Logged out",
        SW: "Umetoka",
        SH: "Umelogout",
        FR: "Déconnecté",
        AR: "تم تسجيل الخروج",
        PT: "Desconectado",
    },
    action_completed: {
        EN: "Done",
        SW: "Imekamilika",
        SH: "Done",
        FR: "Terminé",
        AR: "تم",
        PT: "Concluído",
    },
};

/**
 * Resolve a translated toast string.
 *   key   — one of the TABLE keys (see above)
 *   lang  — user's preferred_language (any casing). Falls back to EN.
 */
export function t(key, lang) {
    const row = TABLE[key];
    if (!row) return key;
    const code = (lang || "EN").toString().toUpperCase();
    return row[code] || row.EN;
}

/**
 * Hook flavour — reads the caller's preferred language from AuthContext
 * so a component can call `const { t } = useToastCopy(); toast(t("saved"))`
 * without manually threading `user.preferred_language` every time.
 */
export function useToastCopy() {
    const { user } = useAuth();
    const lang = user?.preferred_language;
    return {
        lang: (lang || "EN").toString().toUpperCase(),
        t:    (key) => t(key, lang),
    };
}

/** Returns true if a translation exists for the given language code. */
export function hasTranslation(lang) {
    const code = (lang || "EN").toString().toUpperCase();
    return Object.values(TABLE).some((row) => row[code] !== undefined);
}
